// AGC: MainLayout — App shell for routed screens (BoviSense pattern)
// AGC: Provides AppHeader + BottomNav (mobile) or DesktopSidebar (desktop) + Outlet
// AGC: Reuses existing shell components — only changes who provides the navigation callbacks
// AGC: Voice recording + field suggestion confirm fully wired (v3.0.3)
import React, { useState, useCallback, useRef } from 'react'
import { AnimatePresence } from 'framer-motion'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useIsDesktopLayout } from '../../hooks/useIsDesktopLayout'
import { useAuth } from '../../hooks/useAuth'
import { useNotifications } from '../../hooks/useNotifications'
import { useTranslation } from '../../hooks/useTranslation'
import { useVoiceRecording } from '../../hooks'
import { AppHeader } from '../layout/AppHeader'
import { AppDrawer } from '../layout/AppDrawer'
import { BottomNav } from '../shared/BottomNav'
import DesktopSidebar from '../shared/DesktopSidebar'
import { getDesktopWidthClass } from '../shared/desktopPageWidths'
import {
  VoiceRecordingModal,
  VoiceCommandHelpModal,
  shouldShowVoiceHelp,
  setVoiceHelpPreference,
} from '../voice'
import { VoiceRecordingBar } from '../voice/VoiceRecordingBar'
import type { VoiceHelpPreference } from '../voice'
import { useVoiceQueueCount, useVoiceOnlineStatus, useVoiceMode } from '../../context'
import { getBackendUrl } from '../../utils/backendUrl'
import type { NavTab, Screen } from '../../types'
import { APP_VERSION } from '@droidfarm/shared'

// AGC: Map NavTab to route path
const NAV_TO_ROUTE: Record<NavTab, string> = {
  dashboard: '/',
  fields: '/lotes',
  tasks: '/tasks',
  weather: '/weather',
}

// AGC: Map Screen to route path (for AppDrawer + DesktopSidebar farm sections)
const SCREEN_TO_ROUTE: Record<string, string> = {
  dashboard: '/',
  profile: '/profile',
  notifications: '/notifications',
  settings: '/settings',
  'sistema-settings': '/settings/sistema',
  'categorias-settings': '/settings/categorias',
  'tarefas-settings': '/settings/tarefas',
  'maquinario-settings': '/settings/maquinario',
  'culturas-settings': '/settings/culturas',
  'voice-calibration': '/settings/voice-calibration',
  'lotes-list': '/lotes',
  tasks: '/tasks',
  weather: '/weather',
  finances: '/finances',
  expenses: '/expenses',
  inventory: '/inventory',
  machinery: '/machinery',
  'team-users': '/team',
  sorteios: '/sorteios',
  subscription: '/subscription',
  'advisor-marketplace': '/advisors',
  consultations: '/consultations',
}

// AGC: Paths where the BottomNav is hidden — screens with their own fixed-bottom buttons
// AGC: These screens have "Continuar", "Confirmar", or other action buttons pinned to the
// AGC: bottom of the viewport. Showing BottomNav would cover those buttons.
const FULL_SCREEN_PATHS = [
  '/lotes/new',             // AGC: NovoLote + sub-paths (GPS, AI, auto-detect)
  '/lotes/finalize',        // AGC: FinalizarLote (name + farm confirmation)
  '/lotes/polygon',         // AGC: Polygon drawing map
  '/lotes/crop-suggestions',// AGC: Crop recommendations after lot creation
  '/lotes/task-planning',   // AGC: Task planning after crop selection
  '/settings/voice-calibration',
]

function isFullScreenPath(pathname: string): boolean {
  return FULL_SCREEN_PATHS.some(p => pathname.startsWith(p))
}

function deriveNavFromPath(pathname: string): NavTab {
  if (pathname.startsWith('/lotes') || pathname.startsWith('/fields')) return 'fields'
  if (pathname.startsWith('/tasks')) return 'tasks'
  if (pathname.startsWith('/weather')) return 'weather'
  return 'dashboard'
}

function deriveScreenFromPath(pathname: string): Screen {
  if (pathname.startsWith('/lotes')) return 'lotes-list'
  if (pathname.startsWith('/tasks')) return 'tasks'
  if (pathname.startsWith('/weather')) return 'weather'
  if (pathname.startsWith('/finances')) return 'finances'
  if (pathname.startsWith('/expenses')) return 'expenses'
  if (pathname.startsWith('/inventory')) return 'inventory'
  if (pathname.startsWith('/machinery')) return 'machinery'
  if (pathname.startsWith('/team')) return 'team-users'
  if (pathname.startsWith('/settings')) return 'settings'
  if (pathname.startsWith('/profile')) return 'profile'
  if (pathname.startsWith('/notifications')) return 'notifications'
  return 'dashboard'
}

export const MainLayout: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isDesktopLayout = useIsDesktopLayout()
  const { user, getAuthToken } = useAuth()
  const { t, language } = useTranslation()
  const { unreadCount } = useNotifications()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  // AGC: Voice context
  const voiceQueueCount = useVoiceQueueCount()
  const isVoiceOnline = useVoiceOnlineStatus()
  const { mode: voiceMode } = useVoiceMode()

  // AGC: Voice modal visibility state
  const [isVoiceModalVisible, setIsVoiceModalVisible] = useState(false)
  const [pendingBlob, setPendingBlob] = useState<Blob | null>(null)
  const [showVoiceHelpModal, setShowVoiceHelpModal] = useState(false)
  const [recordingMode, setRecordingMode] = useState<'idle' | 'tap' | 'hold'>('idle')

  // AGC: Accumulated field values for voice command interactive confirmation
  // AGC: Tracks values entered by user for each field before final creation
  const [accumulatedFieldValues, setAccumulatedFieldValues] = useState<Record<string, string | number>>({})
  const isSubmittingRef = useRef(false)

  const activeNav = deriveNavFromPath(location.pathname)
  const activeScreen = deriveScreenFromPath(location.pathname)
  const showBottomNav = !isFullScreenPath(location.pathname)

  // AGC: Handle voice result — auto-created entries (no interactive confirmation needed)
  const handleVoiceResult = useCallback((response: { entryType?: string; entry?: { name?: string }; action?: string }) => {
    if (response.entryType === 'field' && response.entry) {
      window.dispatchEvent(new CustomEvent('refresh-plots'))
      navigate('/lotes')
    }
    if (response.entryType === 'scan_product' || response.action === 'open_camera_scan') {
      setIsVoiceModalVisible(false)
      navigate('/inventory')
    }
  }, [navigate])

  // AGC: Core voice recording hook
  const {
    state: voiceState,
    isRecording,
    duration,
    progress,
    error: voiceError,
    lastResponse,
    isPaused,
    analyserNode,
    startRecording,
    stopRecording,
    stopAndGetBlob,
    cancelRecording,
    pauseRecording,
    resumeRecording,
    reset: resetVoice,
  } = useVoiceRecording({
    maxDuration: 60,
    userId: user?.uid || null,
    onProcessingComplete: (response) => {
      if (response.success) {
        handleVoiceResult(response as Parameters<typeof handleVoiceResult>[0])
      }
    },
  })

  const handleMicPress = useCallback(() => {
    // AGC: I1 — Guard with shouldShowVoiceHelp() so first-time users see the help overlay
    if (shouldShowVoiceHelp()) {
      setShowVoiceHelpModal(true)
      return
    }
    setRecordingMode('tap')
    startRecording()
  }, [startRecording])

  const handleMicLongPress = useCallback(() => {
    if (shouldShowVoiceHelp()) {
      setShowVoiceHelpModal(true)
      return
    }
    setRecordingMode('hold')
    startRecording()
  }, [startRecording])

  // AGC: Hold-mode release — stop recorder, pass blob to modal via pendingBlob prop
  const handleMicLongPressEnd = useCallback(async () => {
    if (recordingMode !== 'hold') return
    setRecordingMode('idle')
    const blob = await stopAndGetBlob()
    setPendingBlob(blob)
    setIsVoiceModalVisible(true)
  }, [recordingMode, stopAndGetBlob])

  // AGC: Recording bar — send: stop hook's recorder, pass blob to modal
  const handleBarSend = useCallback(async () => {
    setRecordingMode('idle')
    const blob = await stopAndGetBlob()
    setPendingBlob(blob)
    setIsVoiceModalVisible(true)
  }, [stopAndGetBlob])

  const handleBarCancel = useCallback(() => {
    cancelRecording()
    setRecordingMode('idle')
  }, [cancelRecording])

  // AGC: Recording bar — hold-mode release = send via pendingBlob
  const handleBarRelease = useCallback(async () => {
    setRecordingMode('idle')
    const blob = await stopAndGetBlob()
    setPendingBlob(blob)
    setIsVoiceModalVisible(true)
  }, [stopAndGetBlob])

  const handleBarSlideCancel = useCallback(() => {
    cancelRecording()
    setRecordingMode('idle')
  }, [cancelRecording])

  const handleVoiceHelpPreference = useCallback((preference: VoiceHelpPreference) => {
    setVoiceHelpPreference({
      preference,
      ...(preference === 'weekly' ? { lastShownAt: new Date().toISOString() } : {}),
    })
    setShowVoiceHelpModal(false)
    // AGC: I2 — Use 'tap' mode (mic button tap) consistent with handleMicPress
    setRecordingMode('tap')
    startRecording()
  }, [startRecording])

  const handleVoiceHelpClose = useCallback(() => {
    setShowVoiceHelpModal(false)
    // AGC: I2 — Use 'tap' mode (mic button tap) consistent with handleMicPress
    setRecordingMode('tap')
    startRecording()
  }, [startRecording])

  const handleVoiceModalClose = useCallback(() => {
    setIsVoiceModalVisible(false)
    resetVoice()
    setAccumulatedFieldValues({})
  }, [resetVoice])

  const handleStopRecording = useCallback(() => { stopRecording() }, [stopRecording])

  const handleCancelRecording = useCallback(() => {
    cancelRecording()
    setIsVoiceModalVisible(false)
    resetVoice()
  }, [cancelRecording, resetVoice])

  // AGC: v3.9.42 — Silent cancel: stop the hook's recorder without closing the modal.
  // AGC: Used by v2 agent flow to release the mic between turns.
  const handleSilentCancel = useCallback(() => {
    cancelRecording()
    resetVoice()
  }, [cancelRecording, resetVoice])

  // ============================================================
  // AGC: Voice field suggestion selection — accumulates per-field
  // AGC: choices and auto-creates entry when all required fields filled
  // ============================================================
  const handleFieldSuggestionSelect = useCallback(async (fieldName: string, selectedValue: string, _label: string) => {
    if (!lastResponse?.parsedData || !lastResponse?.fieldSuggestions) return

    const newAccumulated = { ...accumulatedFieldValues, [fieldName]: selectedValue }
    setAccumulatedFieldValues(newAccumulated)

    const updatedData = { ...lastResponse.parsedData, ...newAccumulated }
    const fieldSuggestions = lastResponse.fieldSuggestions
    const requiredFields = lastResponse.entryType === 'task'
      ? ['category', 'dueDate']
      : Object.entries(fieldSuggestions)
          .filter(([, info]) => !(info as { optional?: boolean }).optional)
          .map(([key]) => key)

    const missingFields = requiredFields.filter(field => {
      const v = updatedData[field]
      const hasVal = v !== undefined && v !== null && v !== '' && String(v).toLowerCase() !== 'undefined'
      return !hasVal && newAccumulated[field] === undefined
    })

    if (missingFields.length > 0) return

    // AGC: All required fields filled — delegate to handleConfirmFields
    await handleConfirmFields(updatedData as Record<string, string | number | null>)
  }, [lastResponse, accumulatedFieldValues])

  // ============================================================
  // AGC: Voice confirm all fields — creates entity via backend API
  // AGC: Handles: field, task, vehicle, revenue, expense
  // ============================================================
  const handleConfirmFields = useCallback(async (mergedData: Record<string, string | number | null>) => {
    if (!lastResponse?.parsedData || !user?.uid) return
    if (isSubmittingRef.current) return
    isSubmittingRef.current = true

    const entryType = lastResponse.entryType
    const backendUrl = getBackendUrl()
    const data: Record<string, unknown> = { ...lastResponse.parsedData, ...mergedData }

    // AGC: Compute userEdits for ML correction learning
    const userEdits: Record<string, { from: unknown; to: unknown }> = {}
    const originalParsed = lastResponse.parsedData || {}
    for (const key of Object.keys(data)) {
      if (key.startsWith('_') || key === 'fieldSuggestions' || key === 'navigateToTaskCreation') continue
      const origVal = originalParsed[key]
      const newVal = data[key]
      if (newVal !== null && newVal !== undefined && newVal !== '' && origVal !== newVal) {
        userEdits[key] = { from: origVal ?? null, to: newVal }
      }
    }

    // AGC: ── FIELD ──────────────────────────────────────────────
    if (entryType === 'field') {
      handleVoiceModalClose()
      navigate('/lotes/new', {
        state: {
          lotName: String(data.name || ''),
          selectedFieldId: data.selectedFieldId ?? data.fieldId,
          fieldId: data.selectedFieldId ?? data.fieldId,
          voicePrefillArea: data.area != null ? Number(data.area) : undefined,
          voicePrefillCrop: data.cropType ? String(data.cropType) : undefined,
        },
      })
      toast.success(t('voice.fieldDetected', 'Talhão detectado! Escolha como criar os limites.'))
      isSubmittingRef.current = false
      return
    }

    // AGC: ── TASK ───────────────────────────────────────────────
    if (entryType === 'task') {
      const fs = lastResponse?.fieldSuggestions || {}
      const effectiveData = { ...data } as Record<string, unknown>
      for (const key of ['category', 'dueDate']) {
        const v = effectiveData[key]
        const isEmpty = v === undefined || v === null || v === '' || v === 'none' || String(v).toLowerCase() === 'undefined'
        if (isEmpty && fs[key] && typeof fs[key] === 'object') {
          const info = fs[key] as { value?: string; originalValue?: string }
          const fromFs = info.value ?? info.originalValue
          if (fromFs) effectiveData[key] = fromFs
        }
      }
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      const effectiveCategory = effectiveData.category && String(effectiveData.category).toLowerCase() !== 'undefined'
        ? String(effectiveData.category) : 'outros'
      const effectiveDueDate = effectiveData.dueDate && String(effectiveData.dueDate).length >= 10
        ? String(effectiveData.dueDate) : tomorrow.toISOString().split('T')[0]

      if (data.field === '__new__') {
        handleVoiceModalClose()
        navigate('/lotes/new')
        toast(t('voice.createFieldFirst', 'Crie o campo primeiro, depois retorne para criar a tarefa.'), { icon: '🗺️' })
        isSubmittingRef.current = false
        return
      }

      const priorityMap: Record<string, string> = { alta: 'high', media: 'medium', baixa: 'low' }
      const SELF_PHRASES = ['self', 'eu', 'eu mesmo', 'eu mesma', 'myself', 'yo', 'yo mismo', 'yo misma', 'me']
      const rawAssignee = String(data.assignee || 'self').toLowerCase().trim()
      const isSelf = SELF_PHRASES.includes(rawAssignee)
      const assigneeName = isSelf ? (user.displayName || user.email || 'Eu') : String(data.assigneeName || data.assignee || 'Não atribuído')
      const resolvedAssigneeId = isSelf ? user.uid : String(data.assignee || '')

      // AGC: Resolve field from suggestions
      let taskFieldId = ''
      let taskFieldName = String(data.fieldName || '')
      if (data.field && data.field !== '__new__') {
        const fieldSugs = (lastResponse?.fieldSuggestions?.field as { suggestions?: Array<{ value: string; label: string }> })?.suggestions || []
        const matched = fieldSugs.find(s => s.value === data.field || s.label.toLowerCase() === String(data.field).toLowerCase())
        if (matched) { taskFieldId = matched.value; taskFieldName = matched.label }
        else {
          const fuzzy = fieldSugs.find(s => s.label.toLowerCase().includes(String(data.field).toLowerCase()))
          if (fuzzy) { taskFieldId = fuzzy.value; taskFieldName = fuzzy.label }
          else { taskFieldId = data.fieldId ? String(data.fieldId) : ''; taskFieldName = String(data.field || '') }
        }
      } else if (data.fieldId) { taskFieldId = String(data.fieldId) }

      // AGC: Parse multi-select machinery + inputs
      let selectedMachineryArray: Array<{ id: string; name: string; category: string }> = []
      if (data.selectedMachinery && data.selectedMachinery !== 'none') {
        try { const parsed = JSON.parse(String(data.selectedMachinery)); if (Array.isArray(parsed)) selectedMachineryArray = parsed } catch {}
      }
      if (selectedMachineryArray.length === 0 && data.machinery && data.machinery !== 'none') {
        selectedMachineryArray = [{ id: String(data.machinery), name: String(data.machineryName || ''), category: '' }]
      }
      let selectedInputs: unknown[] = []
      try { if (data.selectedInputs) selectedInputs = JSON.parse(String(data.selectedInputs)) } catch {}

      try {
        const token = await getAuthToken()
        if (!token) { toast.error(t('auth.sessionExpired')); return }
        const response = await fetch(`${backendUrl}/api/v1/voice/create-entry`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            entryType: 'task',
            data: {
              title: String(data.title || 'Tarefa'),
              description: String(data.description || ''),
              category: effectiveCategory,
              priority: priorityMap[String(data.priority || 'media')] || String(data.priority || 'medium'),
              status: 'pending', progress: 0,
              dueDate: effectiveDueDate,
              fieldId: taskFieldId, fieldName: taskFieldName,
              assignedTo: assigneeName, assignedToId: resolvedAssigneeId,
              machinery: selectedMachineryArray, inputs: selectedInputs,
            },
            transcription: lastResponse?.transcription || '',
            userId: user.uid,
            matchId: lastResponse?.matchId,
            userEdits: Object.keys(userEdits).length > 0 ? userEdits : undefined,
          }),
        })
        if (response.ok) {
          toast.success(`Tarefa "${data.title || 'Tarefa'}" criada!`, { duration: 3000, icon: '📋' })
          window.dispatchEvent(new CustomEvent('task-created'))
        } else { toast.error('Erro ao criar tarefa. Tente novamente.') }
      } catch { toast.error('Erro ao criar tarefa. Tente novamente.') }

      setAccumulatedFieldValues({})
      handleVoiceModalClose()
      navigate('/tasks')
      isSubmittingRef.current = false
      return
    }

    // AGC: ── VEHICLE ────────────────────────────────────────────
    if (entryType === 'vehicle' || entryType === 'vehicle:create') {
      const vehicleCategory = String(data.category || data.type || 'outros')
      const vehicleBrand = String(data.brand || '')
      const categoryLabels: Record<string, string> = {
        trator: 'Trator', colheitadeira: 'Colheitadeira', plantadeira: 'Plantadeira',
        pulverizador: 'Pulverizador', distribuidor: 'Distribuidor', implemento: 'Implemento',
        irrigacao: 'Irrigação', transporte: 'Transporte', outros: 'Outros',
      }
      const vehicleName = vehicleBrand
        ? `${categoryLabels[vehicleCategory] || vehicleCategory} ${vehicleBrand}`.trim()
        : String(data.name || 'Novo veículo')
      const statusMap: Record<string, string> = {
        disponivel: 'available', 'disponível': 'available', 'em-uso': 'in_use',
        'em uso': 'in_use', manutencao: 'maintenance', 'manutenção': 'maintenance', inativo: 'maintenance',
      }

      try {
        const token = await getAuthToken()
        if (!token) { toast.error(t('auth.sessionExpired')); return }
        const response = await fetch(`${backendUrl}/api/v1/voice/create-entry`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            entryType: 'vehicle:create',
            data: {
              name: vehicleName, category: vehicleCategory, brand: vehicleBrand,
              model: data.model || '', year: data.year != null ? Number(data.year) : null,
              horimeterHours: data.horimeterHours != null ? Number(data.horimeterHours) : 0,
              width: data.width != null ? Number(data.width) : null,
              tankCapacity: data.tankCapacity != null ? Number(data.tankCapacity) : null,
              status: statusMap[String(data.status || '').toLowerCase()] || 'available',
            },
            transcription: lastResponse?.transcription || '',
            userId: user.uid,
            matchId: lastResponse?.matchId,
            userEdits: Object.keys(userEdits).length > 0 ? userEdits : undefined,
          }),
        })
        if (response.ok) {
          const result = await response.json()
          toast.success(`${result.name || vehicleName} criado com sucesso!`, { duration: 3000, icon: '🚜' })
          window.dispatchEvent(new CustomEvent('refresh-machines'))

          // AGC: Save new brand to voice values if custom
          if (vehicleBrand && (lastResponse?.fieldSuggestions?.brand as { isBrandSelector?: boolean })?.isBrandSelector) {
            const knownBrands = ((lastResponse?.fieldSuggestions?.brand as { suggestions?: Array<{ value: string }> })?.suggestions || [])
            if (!knownBrands.some(s => s.value === vehicleBrand)) {
              const brandCatKey = (lastResponse?.fieldSuggestions?.brand as { brandCategory?: string })?.brandCategory
              if (brandCatKey) {
                fetch(`${backendUrl}/api/v1/patterns/learned-values`, {
                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ fieldType: brandCatKey, value: vehicleBrand.toLowerCase(), label: vehicleBrand, aliases: [vehicleBrand.toLowerCase().replace(/[\s\-_]/g, '')] }),
                }).catch(() => {})
              }
            }
          }
        } else { toast.error('Erro ao criar veículo. Tente novamente.') }
      } catch { toast.error('Erro ao criar veículo. Tente novamente.') }

      setAccumulatedFieldValues({})
      handleVoiceModalClose()
      navigate('/machinery')
      isSubmittingRef.current = false
      return
    }

    // AGC: ── REVENUE / EXPENSE ──────────────────────────────────
    if (entryType === 'revenue' || entryType === 'expense') {
      const revenueValue = data.quantity != null && Number(data.quantity) > 0
        ? Number(data.quantity) : (data.value != null ? Number(data.value) : 0)
      const revenueTitle = String(data.title || (entryType === 'revenue' ? 'Receita via voz' : 'Despesa via voz'))
      const revenueCategory = String(data.category || 'other')

      if (!revenueValue || revenueValue <= 0) {
        toast.error(t('financial.priceRequired', 'Valor é obrigatório. Informe o preço.'), { duration: 4000 })
        isSubmittingRef.current = false
        return
      }

      let financialMachinery: Array<{ id: string; name: string; category: string }> = []
      if (data.selectedMachinery && data.selectedMachinery !== 'none') {
        try { const p = JSON.parse(String(data.selectedMachinery)); if (Array.isArray(p)) financialMachinery = p } catch {}
      }

      try {
        const token = await getAuthToken()
        if (!token) { toast.error(t('auth.sessionExpired')); return }
        const response = await fetch(`${backendUrl}/api/v1/voice/create-entry`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            entryType,
            data: {
              // AGC: Pass ALL parsedData fields through — prevents "field extracted but never sent" bugs
              ...data,
              // AGC: Override computed/coerced values
              title: revenueTitle, value: revenueValue, category: revenueCategory,
              date: data.date || new Date().toISOString(), notes: String(data.notes || ''),
              itemQuantity: data.itemQuantity != null ? Number(data.itemQuantity) : undefined,
              selectedMachinery: financialMachinery.length > 0 ? JSON.stringify(financialMachinery) : undefined,
              selectedInputs: data.selectedInputs ? String(data.selectedInputs) : undefined,
              soldFieldId: revenueCategory === 'land_sale' ? (data.fieldId || data.field) : undefined,
            },
            transcription: lastResponse?.transcription || '',
            userId: user.uid,
            matchId: lastResponse?.matchId,
            userEdits: Object.keys(userEdits).length > 0 ? userEdits : undefined,
          }),
        })
        if (response.ok) {
          const entryResult = await response.json()
          const icon = entryType === 'revenue' ? '💵' : '💰'
          const label = entryType === 'revenue' ? 'Receita' : 'Despesa'
          toast.success(`${icon} ${label} "${revenueTitle}" registrada!`, { duration: 3000 })

          // AGC: Inventory sync feedback toasts
          const sync = entryResult?.inventorySync || entryResult?.entry?.inventorySync
          if (sync) {
            const msgs: Record<string, string> = {
              stock_added: `📦 ${sync.itemName || 'Item'} adicionado ao estoque (total: ${sync.newQty ?? '?'})`,
              item_created: `📦 Novo item "${sync.itemName || 'Item'}" adicionado ao inventário`,
              routed_to_tank: `⛽ ${sync.fuelName || 'Combustível'} adicionado ao ${sync.tankLabel || 'tanque'} (${sync.fillPct ?? '?'}%)`,
              stock_deducted: `📤 ${sync.deducted ?? '?'}x ${sync.itemName || 'Item'} deduzido do estoque${sync.partial ? ' (parcial)' : ''}`,
              machine_archived: `🚜 ${sync.itemName || 'Máquina'} marcada como vendida`,
              field_archived: `🌾 ${sync.itemName || 'Talhão'} marcado como vendido`,
              entry_recorded: `📥 Entrada: ${sync.quantity ?? '?'}x ${sync.itemName || 'Item'} (total: ${sync.newQty ?? '?'})`,
              exit_recorded: `📤 Saída: ${sync.quantity ?? '?'}x ${sync.itemName || 'Item'}${sync.partial ? ' (parcial)' : ''}`,
            }
            const msg = msgs[sync.action as string]
            if (msg) toast.success(msg, { duration: 4000 })
          }

          window.dispatchEvent(new CustomEvent('refresh-finances'))
          window.dispatchEvent(new CustomEvent('refresh-inventory'))
          window.dispatchEvent(new CustomEvent('refresh-machines'))
          window.dispatchEvent(new CustomEvent('refresh-fields'))
        } else { toast.error(`Erro ao registrar ${entryType === 'revenue' ? 'receita' : 'despesa'}. Tente novamente.`) }
      } catch { toast.error(`Erro ao registrar ${entryType === 'revenue' ? 'receita' : 'despesa'}. Tente novamente.`) }

      setAccumulatedFieldValues({})
      handleVoiceModalClose()
      navigate('/finances')
      isSubmittingRef.current = false
      return
    }

    // AGC: Unknown entry type — just close
    isSubmittingRef.current = false
  }, [lastResponse, user, handleVoiceModalClose, navigate, t])

  // AGC: BottomNav / DesktopSidebar tab navigation
  const handleNavClick = useCallback((tab: NavTab) => {
    navigate(NAV_TO_ROUTE[tab])
  }, [navigate])

  // AGC: Screen-level navigation (AppDrawer, DesktopSidebar farm sections)
  const handleScreenNavigate = useCallback((screen: Screen) => {
    const route = SCREEN_TO_ROUTE[screen]
    if (route) {
      navigate(route)
    } else {
      navigate(`/legacy/${screen}`)
    }
    setIsDrawerOpen(false)
  }, [navigate])

  const userName = user?.profile?.displayName || user?.displayName || ''
  const userAvatar = user?.profile?.photoURL || user?.photoURL || undefined
  const userEmail = user?.email || undefined
  const contentWidthClass = getDesktopWidthClass(activeScreen)

  // AGC: Shared voice modals — rendered once regardless of layout branch
  const voiceModals = (
    <>
      <VoiceCommandHelpModal
        isVisible={showVoiceHelpModal}
        onClose={handleVoiceHelpClose}
        onPreferenceChoose={handleVoiceHelpPreference}
      />
      <VoiceRecordingModal
        isVisible={isVoiceModalVisible}
        state={voiceState}
        progress={progress}
        duration={duration}
        error={voiceError}
        lastResponse={lastResponse}
        onClose={() => { handleVoiceModalClose(); setPendingBlob(null) }}
        onStopRecording={handleStopRecording}
        onCancelRecording={handleCancelRecording}
        onSilentCancel={handleSilentCancel}
        onFieldSuggestionSelect={handleFieldSuggestionSelect}
        onConfirmFields={handleConfirmFields}
        voiceMode={voiceMode}
        isOnline={isVoiceOnline}
        queueCount={voiceQueueCount}
        processingOnly
        pendingBlob={pendingBlob}
      />
    </>
  )

  if (isDesktopLayout) {
    return (
      <div className="h-screen w-full grid grid-cols-[240px_minmax(0,1fr)] bg-[#fafbfc] dark:bg-gray-900 transition-colors overflow-hidden">
        <DesktopSidebar
          active={activeNav}
          onNavigate={handleNavClick}
          onNavigateScreen={handleScreenNavigate}
          activeScreen={activeScreen}
          onMicPress={handleMicPress}
          onMicLongPress={handleMicLongPress}
          onMicLongPressEnd={handleMicLongPressEnd}
          onMenuClick={() => setIsDrawerOpen(true)}
          onNotificationsClick={() => navigate('/notifications')}
          notificationCount={unreadCount}
        />

        <div className="flex flex-col h-screen overflow-hidden">
          <main className="flex-1 overflow-y-auto overflow-x-hidden">
            <div className={`${contentWidthClass} mx-auto px-6 py-6`}>
              <Outlet />
            </div>
          </main>
        </div>

        <AppDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          currentScreen={activeScreen}
          onNavigate={handleScreenNavigate}
          userName={userName}
          userAvatar={userAvatar}
          userEmail={userEmail}
          appVersion={APP_VERSION}
          isOnline={navigator.onLine}
          desktopOffsetLeft={240}
        />

        {/* AGC: I4 — AnimatePresence enables exit animation when recording bar unmounts */}
        <AnimatePresence>
          {recordingMode !== 'idle' && (
            <VoiceRecordingBar
              key="recording-bar"
              mode={recordingMode as 'tap' | 'hold'}
              duration={duration}
              isRecording={isRecording}
              isPaused={isPaused}
              analyserNode={analyserNode}
              onSend={handleBarSend}
              onCancel={handleBarCancel}
              onPause={pauseRecording}
              onResume={resumeRecording}
              onSlideCancel={handleBarSlideCancel}
              onRelease={handleBarRelease}
            />
          )}
        </AnimatePresence>

        {voiceModals}
      </div>
    )
  }

  return (
    <div
      className="h-screen w-full flex flex-col transition-colors bg-[#fafbfc] dark:bg-gray-900 overflow-hidden"
      style={{
        // AGC: Top safe-area owned by AppHeader so the header bg reaches the
        // AGC: status bar without a visible color band.
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
      }}
    >
      <AppHeader
        onMenuClick={() => setIsDrawerOpen(true)}
        onNotificationsClick={() => navigate('/notifications')}
        notificationCount={unreadCount}
      />

      <AppDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        currentScreen={activeScreen}
        onNavigate={handleScreenNavigate}
        userName={userName}
        userAvatar={userAvatar}
        userEmail={userEmail}
        appVersion={APP_VERSION}
        isOnline={navigator.onLine}
      />

      <main className={`flex-1 min-h-0 overflow-y-auto overflow-x-hidden ${showBottomNav ? 'pb-24' : 'pb-0'}`}>
        <Outlet />
      </main>

      {showBottomNav && (
        <BottomNav
          active={activeNav}
          onNavigate={handleNavClick}
          onMicPress={handleMicPress}
          onMicLongPress={handleMicLongPress}
          onMicLongPressEnd={handleMicLongPressEnd}
          onMicSlideCancel={handleBarSlideCancel}
          isRecording={recordingMode !== 'idle'}
          recordingMode={recordingMode}
        />
      )}

      {/* AGC: I4 — AnimatePresence enables exit animation when recording bar unmounts */}
      <AnimatePresence>
        {recordingMode !== 'idle' && (
          <VoiceRecordingBar
            key="recording-bar"
            mode={recordingMode as 'tap' | 'hold'}
            duration={duration}
            isRecording={isRecording}
            isPaused={isPaused}
            analyserNode={analyserNode}
            onSend={handleBarSend}
            onCancel={handleBarCancel}
            onPause={pauseRecording}
            onResume={resumeRecording}
            onSlideCancel={handleBarSlideCancel}
            onRelease={handleBarRelease}
          />
        )}
      </AnimatePresence>

      {voiceModals}
    </div>
  )
}
